/** SUNTRONIX ONE — FIELD SERVICE · Engineer Mobile Workspace.
 * Operations → Field Service. Mobile-first job execution. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Wrench, MapPin, Clock, CheckCircle2, AlertTriangle, Package, ChevronRight,
  PlusCircle, RefreshCw,
} from "lucide-react";
import { JobDetail } from "@/pages/operations/FieldJobDetail";

const BUCKETS = [
  { key: "today", label: "Today", icon: Clock, tint: "bg-teal-100 text-teal-800" },
  { key: "upcoming", label: "Upcoming", icon: ChevronRight, tint: "bg-blue-100 text-blue-800" },
  { key: "overdue", label: "Overdue", icon: AlertTriangle, tint: "bg-rose-100 text-rose-800" },
  { key: "completed", label: "Completed", icon: CheckCircle2, tint: "bg-emerald-100 text-emerald-800" },
  { key: "parts_required", label: "Parts", icon: Package, tint: "bg-amber-100 text-amber-800" },
  { key: "escalated", label: "Escalated", icon: AlertTriangle, tint: "bg-violet-100 text-violet-800" },
];

const STATUS_TINT = {
  SCHEDULED: "bg-slate-100 text-slate-700", ASSIGNED: "bg-blue-100 text-blue-800",
  EN_ROUTE: "bg-cyan-100 text-cyan-800", CHECKED_IN: "bg-teal-100 text-teal-800",
  IN_PROGRESS: "bg-amber-100 text-amber-800", PAUSED: "bg-orange-100 text-orange-800",
  COMPLETED: "bg-emerald-100 text-emerald-800", SIGNED_OFF: "bg-emerald-100 text-emerald-800",
  CLOSED: "bg-slate-200 text-slate-600", CANCELLED: "bg-rose-100 text-rose-700",
};

export default function FieldService() {
  const [home, setHome] = useState(null);
  const [meta, setMeta] = useState(null);
  const [active, setActive] = useState("today");
  const [mine, setMine] = useState(true);
  const [openJob, setOpenJob] = useState(null);
  const [dispatch, setDispatch] = useState(false);

  const load = useCallback(() => {
    api.get(`/field-service/home?mine=${mine}`).then(({ data }) => setHome(data)).catch(() => {});
    api.get("/field-service/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, [mine]);
  useEffect(() => { load(); }, [load]);

  const jobs = home?.buckets?.[active] || [];

  return (
    <div className="mx-auto max-w-[1000px] space-y-4 pb-24" data-testid="field-service-page">
      <header className="flex flex-wrap items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white"><Wrench className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">Field Service</div>
          <div className="text-[11px] text-slate-500">Engineer mobile workspace · {home?.me}</div>
        </div>
        <div className="flex items-center gap-1 rounded-md border border-slate-200 p-0.5 text-[11px]">
          <button onClick={() => setMine(true)} data-testid="fs-scope-mine"
            className={`rounded px-2 py-1 ${mine ? "bg-teal-800 text-white" : "text-slate-600"}`}>My jobs</button>
          <button onClick={() => setMine(false)} data-testid="fs-scope-all"
            className={`rounded px-2 py-1 ${!mine ? "bg-teal-800 text-white" : "text-slate-600"}`}>All</button>
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-700"><RefreshCw className="h-4 w-4" /></button>
        {meta?.can_dispatch && (
          <button onClick={() => setDispatch(true)} data-testid="fs-new-job"
            className="inline-flex items-center gap-1 rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white">
            <PlusCircle className="h-3.5 w-3.5" /> New job
          </button>
        )}
      </header>

      {/* Bucket cards */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="fs-buckets">
        {BUCKETS.map((b) => {
          const Icon = b.icon;
          const n = home?.counts?.[b.key] ?? 0;
          return (
            <button key={b.key} onClick={() => setActive(b.key)} data-testid={`fs-bucket-${b.key}`}
              className={`rounded-xl border p-2.5 text-left transition-colors ${active === b.key ? "border-teal-500 ring-1 ring-teal-300" : "border-slate-200"}`}>
              <div className={`inline-grid h-7 w-7 place-items-center rounded-lg ${b.tint}`}><Icon className="h-3.5 w-3.5" /></div>
              <div className="mt-1.5 text-lg font-semibold text-slate-900">{n}</div>
              <div className="text-[9.5px] uppercase tracking-wide text-slate-500">{b.label}</div>
            </button>
          );
        })}
      </div>

      {/* Job list */}
      <div className="space-y-2" data-testid="fs-job-list">
        {jobs.map((j) => (
          <button key={j.id} onClick={() => setOpenJob(j.id)} data-testid={`fs-job-${j.id}`}
            className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-teal-400">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-slate-400">{j.job_number}</span>
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${STATUS_TINT[j.status] || "bg-slate-100"}`}>{j.status}</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-600">{j.job_type}</span>
                {j.priority === "HIGH" && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700">HIGH</span>}
              </div>
              <div className="mt-1 truncate text-[13px] font-semibold text-slate-900">{j.customer_name || "—"}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                {j.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location}</span>}
                {j.serial && <span className="font-mono">{j.serial}</span>}
                {j.ticket_number && <span>· {j.ticket_number}</span>}
                {j.scheduled_at && <span>· {(j.scheduled_at || "").slice(0, 16).replace("T", " ")}</span>}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-300" />
          </button>
        ))}
        {!jobs.length && (
          <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-[12px] text-slate-400">
            No jobs in {active.replace(/_/g, " ")}.
          </div>
        )}
      </div>

      {openJob && (
        <JobDetail jobId={openJob} meta={meta} onClose={() => setOpenJob(null)} onChange={load} />
      )}
      {dispatch && (
        <DispatchDialog meta={meta} onClose={() => setDispatch(false)} onSaved={() => { setDispatch(false); load(); }} />
      )}
    </div>
  );
}

const dinput = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";

function DispatchDialog({ meta, onClose, onSaved }) {
  const [f, setF] = useState({ job_type: "SERVICE", customer_name: "", contact_name: "",
    contact_phone: "", location: "", site_address: "", serial: "", ticket_number: "",
    engineer_email: "", engineer_name: "", instructions: "", priority: "MEDIUM",
    scheduled_at: "", sla_hours: "" });
  const save = async () => {
    if (!f.job_type) return;
    try {
      await api.post("/field-service/jobs", {
        ...f, sla_hours: f.sla_hours ? Number(f.sla_hours) : null,
        scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString() : null,
      });
      toast.success("Job dispatched"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/40 p-4 pt-10 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="fs-dispatch-dialog">
        <div className="mb-3 text-[13px] font-semibold">Dispatch field job</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[10px] text-slate-500">Job type
            <select value={f.job_type} onChange={(e) => setF({ ...f, job_type: e.target.value })} className={dinput} data-testid="fs-job-type">
              {(meta?.job_types || []).map((t) => <option key={t}>{t}</option>)}
            </select></label>
          <label className="text-[10px] text-slate-500">Priority
            <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className={dinput}>
              {["LOW", "MEDIUM", "HIGH"].map((p) => <option key={p}>{p}</option>)}
            </select></label>
          <label className="text-[10px] text-slate-500">Customer
            <input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className={dinput} data-testid="fs-customer" /></label>
          <label className="text-[10px] text-slate-500">Contact
            <input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} className={dinput} /></label>
          <label className="text-[10px] text-slate-500">Contact phone
            <input value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} className={dinput + " font-mono"} /></label>
          <label className="text-[10px] text-slate-500">Asset serial
            <input value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} className={dinput + " font-mono"} data-testid="fs-serial" /></label>
          <label className="text-[10px] text-slate-500">Ticket #
            <input value={f.ticket_number} onChange={(e) => setF({ ...f, ticket_number: e.target.value })} className={dinput + " font-mono"} /></label>
          <label className="text-[10px] text-slate-500">Location
            <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={dinput} data-testid="fs-location" /></label>
          <label className="col-span-2 text-[10px] text-slate-500">Site address
            <input value={f.site_address} onChange={(e) => setF({ ...f, site_address: e.target.value })} className={dinput} /></label>
          <label className="text-[10px] text-slate-500">Engineer email
            <input value={f.engineer_email} onChange={(e) => setF({ ...f, engineer_email: e.target.value })} className={dinput} data-testid="fs-engineer" /></label>
          <label className="text-[10px] text-slate-500">Engineer name
            <input value={f.engineer_name} onChange={(e) => setF({ ...f, engineer_name: e.target.value })} className={dinput} /></label>
          <label className="text-[10px] text-slate-500">Scheduled at
            <input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} className={dinput} data-testid="fs-scheduled" /></label>
          <label className="text-[10px] text-slate-500">SLA (hours)
            <input type="number" value={f.sla_hours} onChange={(e) => setF({ ...f, sla_hours: e.target.value })} className={dinput} /></label>
          <label className="col-span-2 text-[10px] text-slate-500">Instructions
            <textarea value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} rows={2} className={dinput} /></label>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-slate-200 px-3 py-1.5 text-[11px]">Cancel</button>
          <button onClick={save} data-testid="fs-dispatch-save" className="rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white">Dispatch</button>
        </div>
      </div>
    </div>
  );
}
